import axios from 'axios';
import { config } from './Config';
import { toast } from 'react-toastify';
import { isTokenValid } from './Utils/jwtTokenValidation';
import {
  capturePendingDeepLink,
  clearStoragePreservingDeepLink,
} from './Utils/postAuthRedirect';

const API = axios.create({
  baseURL: config.IAM_SERVICE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to include the JWT token in the headers of every request
API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('jwtToken');

    if (process.env.REACT_APP_PORTAL_MODE === 'trinity') {
      config.headers['X-Zavior-Portal'] = 'trinity';
    }

    if (token) {
      if (isTokenValid(token)) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      else {
        console.error('Token is invalid or expired');
        toast.error("Invalid or expired token. Please log in again.");
        // Preserve a Complete Now deep link across the session wipe so the
        // user still lands on the assignment after re-login.
        try {
          const { pathname, search } = window.location;
          if (pathname === '/adminlist' && search.includes('assignment=')) {
            const params = new URLSearchParams(search);
            params.delete('su');
            capturePendingDeepLink(`${pathname}?${params.toString()}`);
          }
        } catch (e) {
          // ignore
        }
        clearStoragePreservingDeepLink();
        window.location.href = "/";
        // Cancel the request instead of letting it go out unauthenticated:
        // without this, the doomed request races the redirect above and gets
        // aborted mid-flight, which axios reports to the caller as a generic
        // "Network Error" (e.g. "Failed to fetch template pack - Network
        // Error") instead of the real "session expired" reason. Mark it
        // silent so callers using handleApiError (which already checks
        // error.silent) don't toast a second, confusing message on top of
        // the one above.
        const expiredError = new Error('Session expired. Redirecting to login.');
        expiredError.silent = true;
        return Promise.reject(expiredError);
      }
    }

    // Let the browser/axios set multipart boundary (do not send application/json).
    if (config.data instanceof FormData) {
      const headers = config.headers;
      if (headers && typeof headers.set === 'function') {
        headers.set('Content-Type', false);
      } else if (headers) {
        delete headers['Content-Type'];
        delete headers['content-type'];
      }
    }

    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to gracefully handle permission-check outages
API.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      const status = error?.response?.status;
      const data = error?.response?.data;
      const dataMsg = typeof data === 'string'
        ? data
        : (data?.error || data?.message || '');
      const url = error?.config?.url || '';

      const isPermissionProbe = (
        (typeof data === 'string' && (
          data.includes('modelAccess/checkPermission') ||
          data.toLowerCase().includes('checkpermission') ||
          data.toLowerCase().includes('permission')
        )) || (
          typeof dataMsg === 'string' && (
            dataMsg.includes('modelAccess') ||
            dataMsg.toLowerCase().includes('checkpermission') ||
            dataMsg.toLowerCase().includes('permission')
          )
        )
      );

      const isAccessControlEOF = (
        (typeof data === 'string' && data.includes('access-control') && data.includes('EOF')) ||
        (typeof dataMsg === 'string' && dataMsg.includes('access-control') && dataMsg.includes('EOF'))
      );

      if (status === 400 && (isPermissionProbe || isAccessControlEOF)) {
        // Treat as no access: for GET, return empty list to avoid breaking UI
        if ((error?.config?.method || 'get').toLowerCase() === 'get') {
          return Promise.resolve({ data: [] });
        }
        // For non-GET, reject silently with a marker (callers may opt to ignore)
        const silentError = new Error('No access');
        silentError.silent = true;
        return Promise.reject(silentError);
      }
    } catch (_) {
      // fall through to default handler
    }

    return Promise.reject(error);
  }
);
export default API;